import type { BrandPageResponse, BrandsListResponse } from '~/types/Brand';
import type { CatalogResponse, NewResponse, OfferFull, OfferMedium, SaleResponse } from '~/types/Catalog';
import type { CategoriesListResponse } from '~/types/Category';
import type { SearchPageResponse } from '~/types/Search';
import type { FavoriteItemsListResponse } from '~/types/Favorite';
import { cloneStoreState } from '../utils/storeState.ts';

export interface CatalogStore {
  currentPageType: string;
  catalogPageIsLoading: boolean;

  productPageFoldableBlocks: {
    description: boolean;
    characteristics: boolean;
    videos: boolean;
    reviews: boolean;
  };

  selectedOffer: OfferFull;
  selectedSizeId: number | null;

  favoriteOffersIds: number[];
}

export const useCatalogStore = createCatalogStore({
  currentPageType: '',
  catalogPageIsLoading: false,

  productPageFoldableBlocks: {
    description: true,
    characteristics: true,
    videos: true,
    reviews: true,
  },

  selectedOffer: null as unknown as OfferFull,
  selectedSizeId: null,

  favoriteOffersIds: [] as number[],
});

export function createCatalogStore(initialState: CatalogStore) {
  return defineStore('catalog', {
    state: () => cloneStoreState(initialState),

    actions: {
      async getCategories() {
        const response = await this.getCategoriesResponse();

        return response?.data.categories ?? [];
      },

      async getCategoriesResponse() {
        try {
          const { data } = await useCustomFetch<CategoriesListResponse>('/catalog/categories', {}, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getBrands() {
        const response = await this.getBrandsResponse();

        return response?.data.brands ?? [];
      },

      async getBrandsResponse() {
        try {
          const { data } = await useCustomFetch<BrandsListResponse>('/catalog/brands', {}, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getBrandBySlug() {
        try {
          this.catalogPageIsLoading = true;
          const { query, params } = useRoute();

          const { data } = await useCustomFetch<BrandPageResponse>(`/catalog/brands/${params.name}`, { query }, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async getSale() {
        try {
          this.catalogPageIsLoading = true;
          const { query } = useRoute();

          const { data } = await useCustomFetch<SaleResponse>('/catalog/sales', { query }, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async getSaleCollection() {
        try {
          this.catalogPageIsLoading = true;

          const { query, params } = useRoute();
          let slug = params.slug;

          if (slug && Array.isArray(slug)) {
            slug = slug.join('/');
          } else if (!slug) {
            return null;
          }

          const { data } = await useCustomFetch<SaleResponse>(`/catalog/sales/${slug}`, { query }, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e: unknown) {
          const statusCode =
            (e as { statusCode?: number | string; status?: number | string }).statusCode ??
            (e as { status?: number | string }).status;

          if (String(statusCode) === '404') throw e;

          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async getNew() {
        try {
          this.catalogPageIsLoading = true;
          const { query } = useRoute();

          const { data } = await useCustomFetch<NewResponse>('/catalog/new', { query }, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async getFavorites() {
        try {
          this.catalogPageIsLoading = true;
          const { query } = useRoute();

          const data = import.meta.client
            ? await useCustomFetchClient<FavoriteItemsListResponse>('/favorite', { query }, '2')
            : (await useCustomFetch<FavoriteItemsListResponse>('/favorite', { query }, '2')).data.value;

          if (data) {
            this.favoriteOffersIds = data.data.offers.map((offer: OfferMedium) => offer.id);

            return data;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async toggleFavorite(offerId: number) {
        const data = await useCustomFetchClient<{ data: []; success: boolean }>('/favorite', {
          method: 'POST',
          body: { id: offerId, model: 'offer' },
        });

        if (!data?.success) return false;

        if (this.favoriteOffersIds.includes(offerId)) {
          this.favoriteOffersIds = this.favoriteOffersIds.filter((id: number) => id !== offerId);
        } else {
          this.favoriteOffersIds.push(offerId);
        }

        return true;
      },

      async getSearchResults() {
        try {
          this.catalogPageIsLoading = true;
          const { query } = useRoute();

          const roistatVisitId = useCookie('roistat_visit');
          if (roistatVisitId.value) query.roistat = roistatVisitId.value;

          const { data } = await useCustomFetch<SearchPageResponse>('/search', { query }, '2');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async getCatalogData(page?: string, skipNotFound = false) {
        try {
          this.catalogPageIsLoading = true;
          const { query, params } = useRoute();
          let slug = params.slug;

          if (slug && Array.isArray(slug)) {
            slug = slug.join('/');
          } else if (!slug) {
            return null;
          }

          if (page) query.page = page;

          const { data } = await useCustomFetch<CatalogResponse>(`/catalog/show/${slug}`, { query }, '2', skipNotFound);

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        } finally {
          this.catalogPageIsLoading = false;
        }
      },

      async getOffersList(ids: string) {
        try {
          const { data } = await useCustomFetch<OfferMedium[]>(`/offers`, { query: { ids } }, '2');

          if (data.value) {
            return data.value;
          } else {
            return [];
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return [];
        }
      },
    },
  });
}
